import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'

/**
 * POURQUOI UN COMPOSANT, ET PAS DU YAML
 *
 * Une carte glissante n'est pas exprimable en reactive-json : servir une
 * mosaïque de tuiles, suivre le zoom et le déplacement, projeter des
 * coordonnées géographiques en pixels et garder des marqueurs accrochés au
 * terrain sont l'affaire d'une bibliothèque cartographique, qu'il faut
 * INSTANCIER et piloter. Aucun élément, aucune action ni aucune capacité
 * Drupal ne le fait. La primitive reste GÉNÉRIQUE — elle ne sait rien du SDIS,
 * ni d'aucun bloc : on lui passe un centre, un zoom et des points.
 *
 * ELLE N'AJOUTE AUCUNE DÉPENDANCE AU PAQUETAGE : la bibliothèque et sa feuille
 * sont chargées à l'exécution depuis les adresses qu'on lui donne. Ces adresses
 * sont des DONNÉES, donc réglables sans toucher au code — c'est ce qui permet
 * de basculer d'un CDN vers une copie servie par le site (ce qu'exige un site
 * public français) sans redéploiement du front. Si la bibliothèque est déjà
 * présente dans la page (`window.L`), elle est réutilisée telle quelle.
 */

/**
 * Le contenu d'une infobulle Leaflet est une CHAÎNE HTML, insérée telle quelle
 * dans le document : tout ce qui vient de la donnée y est échappé, sans
 * exception. Un nom de centre contenant « & » ou « < » casserait le balisage,
 * et une donnée hostile y exécuterait du script.
 */
export const escapeHtml = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;')

/**
 * Une coordonnée, ou NaN. `Number('')` vaut ZÉRO, pas NaN : sans ce filtre, un
 * centre tout juste ajouté — dont la latitude est encore vide — serait posé au
 * large du golfe de Guinée, au beau milieu de l'océan, sans la moindre erreur.
 */
const coord = (v) => {
  if (v === null || v === undefined) return NaN
  const text = String(v).trim()
  return text === '' ? NaN : Number(text)
}

/**
 * La collection, qu'elle arrive en tableau ou en OBJET INDEXÉ.
 *
 * Une liste qui a traversé un `DataFilter` de reactive-json n'est plus un
 * tableau : le filtre reconstruit `[a, b, c]` en `{0: a, 2: c}` — les indices
 * survivent, le type non. Exiger `Array.isArray` viderait donc la carte dès
 * qu'elle est filtrée en amont, sans lever la moindre erreur.
 */
const asList = (value) => {
  if (Array.isArray(value)) return value
  if (value && typeof value === 'object') return Object.values(value)
  return []
}

/** La table « clé de groupe → couleur », lue sur la liste que le bloc édite. */
export const buildColorTable = (groups, keyField = 'key', colorField = 'color', fallback = '#333333') => {
  const table = {}
  asList(groups).forEach((g) => {
    const key = String(g?.[keyField] ?? '')
    if (key) table[key] = String(g?.[colorField] ?? '') || fallback
  })
  return table
}

/**
 * Les points, tirés de la liste BRUTE du bloc : coordonnées converties, points
 * sans coordonnées écartés, couleur prise sur le groupe, infobulle composée des
 * champs demandés — les vides sautés, tous échappés.
 */
export const buildMarkers = (rawMarkers, spec = {}, colorByGroup = {}) => {
  const {
    latField = 'lat', lngField = 'lng', labelField = 'label',
    groupField = 'group', popupFields = [], defaultColor = '#333333',
  } = spec
  return asList(rawMarkers)
    .map((m) => {
      const group = String(m?.[groupField] ?? '')
      const label = String(m?.[labelField] ?? '')
      const lines = (Array.isArray(popupFields) ? popupFields : [])
        .map((f) => ({ field: String(f), value: String(m?.[f] ?? '') }))
        .filter((l) => l.value !== '')
        .map((l) => `<span class="orbit-map-popup-line" data-field="${escapeHtml(l.field)}">${escapeHtml(l.value)}</span>`)
        .join('')
      return {
        lat: coord(m?.[latField]),
        lng: coord(m?.[lngField]),
        label,
        group,
        color: colorByGroup[group] || defaultColor,
        html: label || lines
          ? `<span class="orbit-map-popup"><strong class="orbit-map-popup-title">${escapeHtml(label)}</strong>${lines}</span>`
          : '',
      }
    })
    .filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lng))
}

/** Une promesse par adresse : deux cartes sur la même page ne chargent qu'une fois. */
const loaders = new Map()

const loadOnce = (libUrl, styleUrl) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('pas de document'))
  }
  if (window.L) return Promise.resolve(window.L)
  if (loaders.has(libUrl)) return loaders.get(libUrl)

  const promise = new Promise((resolve, reject) => {
    if (styleUrl && !document.querySelector(`link[data-leaflet-style="${styleUrl}"]`)) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = styleUrl
      link.dataset.leafletStyle = styleUrl
      document.head.appendChild(link)
    }
    const existing = document.querySelector(`script[data-leaflet-lib="${libUrl}"]`)
    const script = existing ?? document.createElement('script')
    script.addEventListener('load', () => (window.L ? resolve(window.L) : reject(new Error('L absent'))))
    script.addEventListener('error', () => reject(new Error('chargement impossible : ' + libUrl)))
    if (!existing) {
      script.src = libUrl
      script.async = true
      script.dataset.leafletLib = libUrl
      document.head.appendChild(script)
    }
  })
  loaders.set(libUrl, promise)
  return promise
}

/**
 * ReactiveJSON primitive — carte glissante à marqueurs.
 *
 * YAML usage:
 *   - type: LeafletMap
 *     lat: 48.95                  # centre
 *     lng: 4.30
 *     zoom: 9
 *     markers: ~~.centres         # la liste BRUTE, telle que le bloc l'édite
 *     latField: lat               # noms des champs à y lire (valeurs par défaut montrées)
 *     lngField: lng
 *     labelField: label
 *     groupField: group
 *     popupFields: [group, address, phone]   # lignes de l'infobulle, dans l'ordre
 *     groups: ~~.groupes          # [{key, color}] — la table qui COLORE les points
 *     groupKeyField: key
 *     groupColorField: color
 *     filter: ~~.filtre           # n'affiche que les points de ce groupe
 *     filterAll: tous             # …sauf quand le filtre vaut CETTE valeur
 *     libUrl: "https://…/leaflet.js"
 *     styleUrl: "https://…/leaflet.css"
 *     tileUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
 *     attribution: "© OpenStreetMap"
 *     attributes: { class: "absolute inset-0", aria-label: "Carte des centres" }
 *
 * Une seule source de vérité : on lui passe la liste que le bloc édite déjà, et
 * les noms des champs où lire. Rien n'est recopié ni pré-mâché, donc rien ne
 * peut diverger.
 *
 * ACCESSIBILITÉ — les marqueurs d'une carte glissante ne sont ni atteignables
 * au clavier ni restitués par un lecteur d'écran. Ce que la carte montre DOIT
 * donc exister ailleurs dans la page, sous forme de texte : c'est au bloc
 * appelant de le fournir, pas à la primitive de le simuler.
 */
export const LeafletMap = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const lat = Number(resolve(props.lat) ?? 48.95)
  const lng = Number(resolve(props.lng) ?? 4.3)
  const zoom = Number(resolve(props.zoom) ?? 9)
  const filter = String(resolve(props.filter) ?? '')
  const filterAll = String(resolve(props.filterAll) ?? '')
  const rawMarkers = resolve(props.markers)
  const rawGroups = resolve(props.groups)
  const latField = resolve(props.latField) ?? 'lat'
  const lngField = resolve(props.lngField) ?? 'lng'
  const labelField = resolve(props.labelField) ?? 'label'
  const groupField = resolve(props.groupField) ?? 'group'
  const groupKeyField = resolve(props.groupKeyField) ?? 'key'
  const groupColorField = resolve(props.groupColorField) ?? 'color'
  const defaultColor = resolve(props.defaultColor) ?? '#333333'
  const popupFields = Array.isArray(resolve(props.popupFields)) ? resolve(props.popupFields) : []
  const libUrl = resolve(props.libUrl) ?? ''
  const styleUrl = resolve(props.styleUrl) ?? ''
  const tileUrl = resolve(props.tileUrl) ?? ''
  const attribution = resolve(props.attribution) ?? ''
  const evaluatedAttrs = useEvaluatedAttributes(props.attributes ?? {})

  const colorByGroup = useMemo(
    () => buildColorTable(rawGroups, groupKeyField, groupColorField, defaultColor),
    [rawGroups, groupKeyField, groupColorField, defaultColor],
  )

  const markers = useMemo(
    () => buildMarkers(rawMarkers, { latField, lngField, labelField, groupField, popupFields, defaultColor }, colorByGroup),
    [rawMarkers, latField, lngField, labelField, groupField, popupFields, defaultColor, colorByGroup],
  )

  const hostRef = useRef(null)
  const mapRef = useRef(null)
  const layerRef = useRef(null)
  const [failed, setFailed] = useState(false)
  // La carte naît APRÈS le premier rendu (la bibliothèque se charge par le
  // réseau) : sans cet état, l'effet qui pose les points serait déjà passé —
  // sur une couche encore inexistante — et rien ne le rejouerait. La carte
  // s'affichait alors vide jusqu'au premier clic sur un filtre.
  const [ready, setReady] = useState(false)

  // --- Création de la carte, une fois.
  useEffect(() => {
    if (!libUrl || !hostRef.current) return
    let cancelled = false
    loadOnce(libUrl, styleUrl)
      .then((L) => {
        if (cancelled || !hostRef.current || mapRef.current) return
        const map = L.map(hostRef.current, { scrollWheelZoom: true }).setView([lat, lng], zoom)
        if (tileUrl) L.tileLayer(tileUrl, { maxZoom: 18, attribution }).addTo(map)
        mapRef.current = map
        layerRef.current = L.layerGroup().addTo(map)
        setReady(true)
        // La carte naît souvent dans un conteneur qui vient d'apparaître (une
        // pop-in) : sans ce recalcul elle se dessine à une taille périmée et
        // laisse des tuiles grises.
        const resize = () => map.invalidateSize()
        requestAnimationFrame(resize)
        if (typeof ResizeObserver !== 'undefined') {
          const observer = new ResizeObserver(resize)
          observer.observe(hostRef.current)
          map._orbitResizeObserver = observer
        }
      })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => {
      cancelled = true
      if (mapRef.current) {
        mapRef.current._orbitResizeObserver?.disconnect()
        mapRef.current.remove()
        mapRef.current = null
        layerRef.current = null
      }
      setReady(false)
    }
    // Le centre et le zoom sont la position INITIALE : les remettre à chaque
    // changement de données ramènerait le visiteur au point de départ.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libUrl, styleUrl, tileUrl, attribution])

  // --- Les points, rejoués à chaque changement de liste ou de filtre.
  useEffect(() => {
    const L = typeof window !== 'undefined' ? window.L : null
    if (!L || !layerRef.current) return
    layerRef.current.clearLayers()
    markers
      .filter((m) => !filter || filter === filterAll || m.group === filter)
      .forEach((m) => {
        const marker = L.circleMarker([m.lat, m.lng], {
          radius: 8, color: '#fff', weight: 2, fillColor: m.color, fillOpacity: 1,
        })
        if (m.label) marker.bindTooltip(m.label)
        if (m.html) marker.bindPopup(m.html)
        marker.addTo(layerRef.current)
      })
  }, [markers, filter, filterAll, failed, ready])

  const { className, ...rest } = evaluatedAttrs

  return (
    <ActionDependant {...props}>
      <div
        ref={hostRef}
        className={className}
        {...rest}
        // La carte est un objet graphique piloté à la souris : annoncée aux
        // technologies d'assistance, elle ne livrerait qu'un cadre vide. Le
        // contenu équivalent est rendu en texte par le bloc appelant.
        role="presentation"
        aria-hidden="true"
        data-leaflet-failed={failed ? 'true' : undefined}
      />
    </ActionDependant>
  )
}
